/**
 * PageRail — the ONE seam a page calls to put its secondary navigation on the
 * SIDE instead of a top tab strip.
 *
 * Page-level / secondary navigation (a pipeline switch, a section's view tabs,
 * a settings sub-nav) never belongs at the top of the content — it belongs in
 * the right-gutter rail, the way the docs reader and Agents ▸ Analytics already
 * work. A page declares its nav as DATA (`usePageRail({ representation, spec })`);
 * this builds a `side:"right"` rail extension and pushes it through the shell
 * `ContextRail` (the representation rides the rail's `controls` slot, which the
 * ContextRail already renders, so NO shell change is needed). The page renders
 * no nav itself.
 *
 * Options carry an `href` (real `<Link>` navigation — the common case, since
 * most switches are URL-driven: `?pipeline=`, `?view=`) OR fall back to an
 * `onChange` callback for in-page client state.
 *
 * Five representations cover the surfaces (audit: `docs/design/SIDE-NAV-PATTERN.md`):
 *   • `list`      — sliding-marker list (the page-nav default; content below)
 *   • `segmented` — filled pills (a small pick-one switch)
 *   • `grouped`   — flat indented cluster with counts (settings/setup)
 *   • `select`    — a dropdown (many options / tight rail)
 *   • `rich`      — bordered rows with a sub-line + count (high-signal switches)
 *
 * For filter rails (selects/chips/search) pass your own JSX via `controls`; for
 * an on-this-page TOC pass `sections` (rendered by the rail's scroll-spy `toc`).
 * Hidden < lg (the ContextRail is desktop-only) — give a compact in-body
 * fallback there, like docs.
 */

import * as React from "react";
import { RouterLink as Link } from "../router";
import { cn } from "../utils";
import { NavRailMarker, useActiveMarker } from "./nav-rail-marker";
import {
  useProvideRailExtension,
  type RailExtension,
  type RailExtensionTocGroup,
} from "../rail-extension-context";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../select";

export interface RailOption {
  value: string;
  label: string;
  /** Real navigation target — rendered as a `<Link>` (the common, URL-driven case). */
  href?: string;
  /** Pre-rendered icon JSX (sized down here). */
  icon?: React.ReactNode;
  /** At-a-glance trailing count/health (e.g. "12", "$148k"). */
  hint?: string;
  /** Secondary line — rich rows only. */
  sub?: string;
}

export interface RailSpec {
  /** Group heading (e.g. "Pipeline", "View"). */
  label: string;
  /** Active option value. */
  value: string;
  /** In-page state setter — used only for options WITHOUT an `href`. */
  onChange?: (value: string) => void;
  options: RailOption[];
}

export type RailRepresentation = "list" | "segmented" | "grouped" | "select" | "rich";

const RailLabel = ({ children }: { children: React.ReactNode }) => (
  <span className="px-1 type-subtext font-medium uppercase tracking-[0.08em] text-muted-foreground/70">
    {children}
  </span>
);

/** One interactive row — a real `<Link>` when the option has an `href`, else a
 *  client `<button>`. Centralizes the Link-vs-button choice so every
 *  representation shares it (and the one button opt-out). */
function RailItem({
  option,
  active,
  onSelect,
  className,
  children,
}: {
  option: RailOption;
  active: boolean;
  onSelect?: () => void;
  className?: string;
  children: React.ReactNode;
}) {
  if (option.href) {
    return (
      <Link
        href={option.href}
        data-active={active ? "true" : undefined}
        aria-current={active ? "page" : undefined}
        className={className}
      >
        {children}
      </Link>
    );
  }
  return (
    // button-ignore: side-rail switch row (a view/state toggle), not an action button
    <button
      type="button"
      data-active={active ? "true" : undefined}
      aria-current={active ? "true" : undefined}
      onClick={onSelect}
      className={className}
    >
      {children}
    </button>
  );
}

/**
 * RailBody — renders a {@link RailSpec} in a chosen {@link RailRepresentation}.
 * Pure presentation driven by `spec.value`; the caller owns the state.
 */
export function RailBody({
  representation,
  spec,
}: {
  representation: RailRepresentation;
  spec: RailSpec;
}) {
  const { label, value, onChange, options } = spec;

  if (representation === "select") {
    return (
      <div className="flex flex-col gap-2">
        <RailLabel>{label}</RailLabel>
        <Select value={value} onValueChange={(v) => onChange?.(v)}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
                {o.hint ? ` · ${o.hint}` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (representation === "segmented") {
    return (
      <div className="flex flex-col gap-2">
        <RailLabel>{label}</RailLabel>
        <div className="flex flex-col gap-1">
          {options.map((o) => {
            const active = o.value === value;
            return (
              <RailItem
                key={o.value}
                option={o}
                active={active}
                onSelect={() => onChange?.(o.value)}
                className={cn(
                  "flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-left type-subtext transition-colors",
                  active
                    ? "bg-selected font-medium text-foreground"
                    : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground",
                )}
              >
                <span className="flex min-w-0 items-center gap-2 truncate">
                  {o.icon ? (
                    <span className="inline-flex shrink-0 [&_svg]:size-3.5">{o.icon}</span>
                  ) : null}
                  {o.label}
                </span>
                {o.hint ? (
                  <span className="shrink-0 type-subtext tabular-nums text-muted-foreground/70">{o.hint}</span>
                ) : null}
              </RailItem>
            );
          })}
        </div>
      </div>
    );
  }

  if (representation === "rich") {
    return (
      <div className="flex flex-col gap-2">
        <RailLabel>{label}</RailLabel>
        <div className="flex flex-col gap-1.5">
          {options.map((o) => {
            const active = o.value === value;
            return (
              <RailItem
                key={o.value}
                option={o}
                active={active}
                onSelect={() => onChange?.(o.value)}
                className={cn(
                  "flex flex-col gap-0.5 rounded-md border px-2.5 py-2 text-left transition-colors",
                  active
                    ? "border-foreground/25 bg-selected"
                    : "border-border/50 hover:border-border hover:bg-secondary/40",
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-2 truncate type-subtext text-foreground">
                    {o.icon ? (
                      <span className="inline-flex shrink-0 [&_svg]:size-3.5">{o.icon}</span>
                    ) : null}
                    {o.label}
                  </span>
                  {o.hint ? (
                    <span className="shrink-0 type-subtext tabular-nums text-muted-foreground">{o.hint}</span>
                  ) : null}
                </div>
                {o.sub ? <span className="truncate type-subtext text-muted-foreground/70">{o.sub}</span> : null}
              </RailItem>
            );
          })}
        </div>
      </div>
    );
  }

  // list + grouped — the sliding-marker grammar shared with the ModeRail / docs
  // rail. `list` adds the marker that tracks the active row; `grouped` is the
  // flat bold-active cluster (no marker).
  return <MarkerList spec={spec} marker={representation === "list"} />;
}

function MarkerList({ spec, marker }: { spec: RailSpec; marker: boolean }) {
  const { ref, top } = useActiveMarker(spec.value);
  return (
    <div className="flex flex-col gap-2">
      <RailLabel>{spec.label}</RailLabel>
      <div ref={ref} className="relative flex flex-col border-l border-border/40">
        {marker ? <NavRailMarker top={top} /> : null}
        {spec.options.map((o) => {
          const active = o.value === spec.value;
          return (
            <RailItem
              key={o.value}
              option={o}
              active={active}
              onSelect={() => spec.onChange?.(o.value)}
              className={cn(
                "flex items-center justify-between gap-2 py-1 pl-3 pr-2 text-left type-subtext tracking-tight transition-colors",
                active ? "font-medium text-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              <span className="flex min-w-0 items-center gap-2 truncate">
                {o.icon ? (
                  <span className={cn("inline-flex shrink-0 [&_svg]:size-3.5", active ? "opacity-90" : "opacity-50")}>
                    {o.icon}
                  </span>
                ) : null}
                {o.label}
              </span>
              {o.hint ? (
                <span className="shrink-0 type-subtext text-muted-foreground/70">{o.hint}</span>
              ) : null}
            </RailItem>
          );
        })}
      </div>
    </div>
  );
}

export interface PageRailSection {
  text: string;
  anchor: string;
  children?: { text: string; anchor: string }[];
}

export interface PageRailConfig {
  /** Rail aria-label (e.g. "Board", "Sales"). */
  groupLabel: string;
  /** A pick-one switch rendered in `representation` (default "list"). */
  representation?: RailRepresentation;
  spec?: RailSpec;
  /** An on-this-page TOC (rendered by the rail's scroll-spy `toc`). */
  sections?: PageRailSection[];
  /** Arbitrary extra node (filters: selects/chips/search), below the switch. */
  controls?: React.ReactNode;
}

/**
 * usePageRail — push a page's secondary nav into the shell ContextRail while the
 * component is mounted. Pass `null` to render no rail (e.g. a cold-start state).
 *
 * The extension keeps a STABLE identity across renders (derived from scalar deps)
 * so it does not re-push every render — `useProvideRailExtension`'s effect would
 * otherwise loop (its cleanup pushes null, the effect re-pushes). Callers may
 * hand a fresh config object each render; that is fine.
 */
export function usePageRail(config: PageRailConfig | null): void {
  const active = config != null;
  const groupLabel = config?.groupLabel ?? "";
  const representation = config?.representation ?? "list";
  const specLabel = config?.spec?.label ?? "";
  const specValue = config?.spec?.value ?? "";
  const onChange = config?.spec?.onChange;
  const options = config?.spec?.options;
  const sections = config?.sections;
  const extraControls = config?.controls;

  // Re-memo the switch node only when its rendered state changes. `options` is
  // captured here; surfaces pass a stable (module/`useMemo`) options array, and
  // its option set is keyed below so an actual change still re-renders.
  const optionsKey = options
    ? options.map((o) => `${o.value}:${o.href ?? ""}:${o.hint ?? ""}`).join("|")
    : "";
  const switchKey = config?.spec ? `${representation}:${specLabel}:${specValue}:${optionsKey}` : "";
  const switchNode = React.useMemo<React.ReactNode>(() => {
    if (!options) return null;
    return (
      <RailBody
        representation={representation}
        spec={{ label: specLabel, value: specValue, onChange, options }}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [switchKey]);

  const controlsNode = React.useMemo<React.ReactNode>(() => {
    if (!switchNode && !extraControls) return undefined;
    if (switchNode && extraControls) {
      return (
        <div className="flex flex-col gap-7">
          {switchNode}
          {extraControls}
        </div>
      );
    }
    return switchNode ?? extraControls;
  }, [switchNode, extraControls]);

  const tocKey = sections
    ? sections.map((s) => `${s.anchor}>${(s.children ?? []).map((c) => c.anchor).join(",")}`).join("|")
    : "";
  const toc = React.useMemo<RailExtensionTocGroup[]>(
    () =>
      (sections ?? []).map((s) => ({
        text: s.text,
        anchor: s.anchor,
        children: s.children ?? [],
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tocKey],
  );

  const extension = React.useMemo<RailExtension | null>(() => {
    if (!active) return null;
    return {
      side: "right",
      placement: "reserve",
      groupLabel,
      activeHref: "",
      links: [],
      toc,
      controls: controlsNode,
    };
  }, [active, groupLabel, toc, controlsNode]);

  useProvideRailExtension(extension);
}

/**
 * PageRailMount — a render-nothing client bridge so a SERVER page can push a
 * side rail. Pass the same config as {@link usePageRail}; for URL-driven nav the
 * options carry `href`s (plain data, so it crosses the server→client boundary
 * cleanly — no `onChange` function needed). Mirrors the analytics `_rail.tsx`
 * pattern.
 */
export function PageRailMount(config: PageRailConfig): null {
  usePageRail(config);
  return null;
}
